import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Grid,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useMutation } from "@apollo/client";
import { REGISTER_JOTUN_SHOP } from "../graphql/queries";

export default function JotunShopRegistrationPage() {
  const [formData, setFormData] = useState({
    email: "",
    name: "",
    password: "",
    shopName: "",
    shopLocation: "",
    shopContact: "",
  });
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const [registerJotunShop, { loading }] = useMutation(REGISTER_JOTUN_SHOP);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { email, name, password, shopName, shopLocation, shopContact } =
      formData;
    if (
      !email?.trim() ||
      !name?.trim() ||
      !password?.trim() ||
      !shopName?.trim()
    ) {
      setNotification({
        open: true,
        message:
          "Please fill in all required fields (Email, Name, Password, Shop Name)",
        severity: "error",
      });
      return;
    }
    try {
      const result = await registerJotunShop({
        variables: {
          email: email.trim(),
          name: name.trim(),
          password,
          shopName: shopName.trim(),
          shopLocation: shopLocation?.trim() || "",
          shopContact: shopContact?.trim() || "",
        },
      });
      // GraphQL errors (e.g. duplicate email) come in result.errors, not thrown
      const graphQLError = result?.errors?.[0];
      if (graphQLError) {
        const errMsg = graphQLError?.message || "Registration failed. Please try again.";
        setNotification({ open: true, message: errMsg, severity: "error" });
        return;
      }
      const msg = result?.data?.registerJotunShop || "Registration successful.";
      setNotification({ open: true, message: msg, severity: "success" });
      setFormData({
        email: "",
        name: "",
        password: "",
        shopName: "",
        shopLocation: "",
        shopContact: "",
      });
    } catch (error) {
      setNotification({
        open: true,
        message: error?.message || "Registration failed. Please try again.",
        severity: "error",
      });
    }
  };

  const LOGO_URL =
    "https://api-admin.eventegration.com/public/logo/5087644490.png?t=1769929078259";

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "grey.100",
        py: 4,
      }}
    >
      <Paper
        elevation={6}
        sx={{
          maxWidth: 520,
          width: "100%",
          overflow: "hidden",
          borderRadius: 3,
        }}
      >
        <Box sx={{ p: 4 }}>
          {/* Logo & Heading */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "column" },
              alignItems: { xs: "center", sm: "center" },
              gap: 2,
              mb: 2,
              textAlign: { xs: "center", sm: "center" },
            }}
          >
            <Box
              component="img"
              src={LOGO_URL}
              alt="Jotun Logo"
              sx={{
                height: 56,
                width: "auto",
                objectFit: "contain",
              }}
            />
            <Box>
              <Typography variant="h4" fontWeight="bold" color="primary.main">
                Jotun Shop Registration
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                Join the Jotun Partner Network
              </Typography>
            </Box>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Register your shop to participate in the Jotun event. Complete the
            form below to get started.
          </Typography>

          {notification.open && (
            <Alert
              severity={notification.severity}
              onClose={() => setNotification({ ...notification, open: false })}
              sx={{ mb: 2 }}
            >
              {notification.message}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label="Email *"
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label="Name *"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label="Password *"
                  type="password"
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label="Shop Name *"
                  value={formData.shopName}
                  onChange={(e) =>
                    setFormData({ ...formData, shopName: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Shop Location"
                  value={formData.shopLocation}
                  onChange={(e) =>
                    setFormData({ ...formData, shopLocation: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Shop Contact"
                  value={formData.shopContact}
                  onChange={(e) =>
                    setFormData({ ...formData, shopContact: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  size="large"
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : null}
                >
                  {loading ? "Registering..." : "Register"}
                </Button>
              </Grid>
            </Grid>
          </form>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 2, display: "block" }}
          >
            After registration, you will receive an email with your login
            details.
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
}
